import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Loader2, Plus, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const formatLogDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
  });
};

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Admin-only modal that lets the admin add / edit / delete the per-day
 * rep entries for a specific (challenge, user). The leaderboard total is
 * automatically recomputed on every mutation by the backend.
 */
export const AdminDailyLogEditor = ({ challenge, user, api, onClose, onSaved }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingDate, setSavingDate] = useState(null);
  // Per-row editable draft value (keyed by date)
  const [drafts, setDrafts] = useState({});
  // Add-new-entry form
  const [newDate, setNewDate] = useState(todayIso());
  const [newValue, setNewValue] = useState('');
  const [addingNew, setAddingNew] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api().get(`/challenges/${challenge.id}/admin/daily-logs/${user.id}`);
      const list = res.data.logs || [];
      setLogs(list);
      // Seed drafts with current values so the inputs are pre-filled.
      const seeded = {};
      list.forEach((log) => { seeded[log.date] = String(log.value || 0); });
      setDrafts(seeded);
    } catch {
      toast.error('Could not load logs');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge.id, user.id]);

  const saveEntry = async (date) => {
    const raw = drafts[date];
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) {
      toast.error('Enter a number ≥ 0');
      return;
    }
    setSavingDate(date);
    try {
      await api().post(
        `/challenges/${challenge.id}/admin/log-daily/${user.id}`,
        { date, value: Math.floor(n) },
      );
      toast.success(`${user.name}'s ${formatLogDate(date)} → ${n} ${challenge.unit || 'reps'}`);
      await fetchLogs();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSavingDate(null);
    }
  };

  const deleteEntry = async (date) => {
    if (!window.confirm(`Delete ${user.name}'s entry for ${formatLogDate(date)}? This subtracts those reps from their leaderboard total.`)) return;
    setSavingDate(date);
    try {
      await api().delete(`/challenges/${challenge.id}/admin/log-daily/${user.id}/${date}`);
      toast.success(`Removed ${formatLogDate(date)}`);
      await fetchLogs();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    } finally {
      setSavingDate(null);
    }
  };

  const addNewEntry = async () => {
    const n = Number(newValue);
    if (!newDate || !Number.isFinite(n) || n < 0) {
      toast.error('Pick a date and enter a number ≥ 0');
      return;
    }
    setAddingNew(true);
    try {
      await api().post(
        `/challenges/${challenge.id}/admin/log-daily/${user.id}`,
        { date: newDate, value: Math.floor(n) },
      );
      toast.success(`Added ${n} ${challenge.unit || 'reps'} on ${formatLogDate(newDate)}`);
      setNewValue('');
      setNewDate(todayIso());
      await fetchLogs();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add entry');
    } finally {
      setAddingNew(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          className="bg-card border border-border rounded-2xl max-w-lg w-full max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="admin-daily-log-editor"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2 min-w-0">
              <Calendar className="h-5 w-5 text-primary shrink-0" />
              <div className="min-w-0">
                <h3 className="font-heading text-base text-white uppercase tracking-wider truncate">
                  Edit {user.name}'s Daily Log
                </h3>
                <p className="text-xs text-muted-foreground truncate">{challenge.title}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 text-muted-foreground flex items-center justify-center"
              aria-label="Close"
              data-testid="admin-daily-log-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Add new entry */}
          <div className="p-4 border-b border-border bg-primary/5">
            <p className="text-xs text-primary uppercase tracking-widest mb-2 font-heading">Add / Backfill an Entry</p>
            <div className="flex gap-2">
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                max={todayIso()}
                className="bg-black border-border text-white flex-1"
                data-testid="admin-log-new-date"
              />
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="Reps"
                className="bg-black border-border text-white w-24 text-right"
                data-testid="admin-log-new-value"
              />
              <Button
                onClick={addNewEntry}
                disabled={addingNew || !newValue}
                className="bg-primary text-black hover:bg-primary/90 shrink-0"
                data-testid="admin-log-new-save"
              >
                {addingNew ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Backfills an exact (date, reps) entry. If a log already exists on that date, it is overwritten.
            </p>
          </div>

          {/* Existing entries */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 text-primary animate-spin" />
              </div>
            ) : logs.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No logs yet. Add one above.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {logs.map((log) => (
                  <li key={log.date} className="flex items-center gap-2 px-4 py-2.5" data-testid={`admin-log-row-${log.date}`}>
                    <span className="text-white text-sm font-medium w-24 shrink-0">{formatLogDate(log.date)}</span>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={drafts[log.date] ?? ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [log.date]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveEntry(log.date); }}
                      className="bg-black border-border text-white flex-1 text-right"
                      data-testid={`admin-log-input-${log.date}`}
                    />
                    <Button
                      onClick={() => saveEntry(log.date)}
                      disabled={savingDate === log.date}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white shrink-0"
                      data-testid={`admin-log-save-${log.date}`}
                    >
                      {savingDate === log.date ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                    </Button>
                    <button
                      onClick={() => deleteEntry(log.date)}
                      disabled={savingDate === log.date}
                      className="h-8 w-8 rounded-md bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 flex items-center justify-center disabled:opacity-50"
                      aria-label="Delete entry"
                      data-testid={`admin-log-delete-${log.date}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="p-3 border-t border-border text-[11px] text-muted-foreground text-center">
            Changes recompute the leaderboard total automatically.
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
